import React, { useMemo, useState, useCallback } from "react";
import {
	ScatterChart,
	Scatter,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	Cell,
	ReferenceArea,
} from "recharts";

// Emoji shape component - Her olay için emoji gösterir
const EmojiShape = (emoji) => (props) => {
	const { cx, cy } = props;
	return (
		<text
			x={cx}
			y={cy}
			textAnchor="middle"
			dominantBaseline="central"
			fontSize="18"
			style={{ cursor: "pointer" }}
		>
			{emoji}
		</text>
	);
};

// Olay türlerine göre emoji ikonları
const BedInIcon = EmojiShape("🛏️");
const BedOutIcon = EmojiShape("🚶");
const HeartIcon = EmojiShape("❤️");
const HeartLowIcon = EmojiShape("💗");
const LungsIcon = EmojiShape("🫁");
const LungsLowIcon = EmojiShape("💨");
const RotateIcon = EmojiShape("🔄");

// Yeşil döndürme ikonu (SVG tabanlı)
const RotateDetectedIcon = (props) => {
	const { cx, cy } = props;
	return (
		<g transform={`translate(${cx - 10}, ${cy - 10})`}>
			<circle cx="10" cy="10" r="10" fill="#27ae60" />
			<path
				d="M10 4C6.69 4 4 6.69 4 10H2L5 13L8 10H6C6 7.79 7.79 6 10 6C12.21 6 14 7.79 14 10C14 12.21 12.21 14 10 14C8.9 14 7.9 13.55 7.17 12.83L5.76 14.24C6.81 15.29 8.32 16 10 16C13.31 16 16 13.31 16 10C16 6.69 13.31 4 10 4Z"
				fill="white"
				transform="scale(0.85) translate(2, 2)"
			/>
		</g>
	);
};
const MovementIcon = EmojiShape("🏃");
const FallIcon = EmojiShape("⚠️");
const PersonIcon = EmojiShape("👤");
const NoPersonIcon = EmojiShape("👻");
const InBedIcon = EmojiShape("😴");
const AwayFromBedIcon = EmojiShape("🚪");
const CircleShape = EmojiShape("❓");

const EventChart = ({ data }) => {
	// Zoom state'leri
	const [refAreaLeft, setRefAreaLeft] = useState(null);
	const [refAreaRight, setRefAreaRight] = useState(null);
	const [xDomain, setXDomain] = useState(null);
	const [yDomain, setYDomain] = useState(null);
	const [isZooming, setIsZooming] = useState(false);
	const [zoomLevel, setZoomLevel] = useState(1); // Zoom seviyesi

	// VitalDetail.jsx'teki tablo mantığına göre config
	const eventConfig = {
		inBed: {
			color: "#00ff00",
			label: "Yatağa Girdi",
			shape: BedInIcon,
			yValue: 50,
		},
		outOfBed: {
			color: "#ff0000",
			label: "Yataktan Çıktı",
			shape: BedOutIcon,
			yValue: 100,
		},
		"High Heart Rate": {
			color: "#ff1493",
			label: "Yüksek Kalp Atış Hızı",
			shape: HeartIcon,
			yValue: 150,
		},
		"Low Heart Rate": {
			color: "#ff69b4",
			label: "Düşük Kalp Atış Hızı",
			shape: HeartLowIcon,
			yValue: 200,
		},
		"High Respiration Rate": {
			color: "#00ced1",
			label: "Yüksek Solunum Hızı",
			shape: LungsIcon,
			yValue: 250,
		},
		"Low Respiration Rate": {
			color: "#4682b4",
			label: "Düşük Solunum Hızı",
			shape: LungsLowIcon,
			yValue: 300,
		},
		"Posture Change": {
			color: "#9b59b6",
			label: "Bası Yaralarını Önlemek İçin Pozisyon",
			shape: RotateIcon,
			yValue: 350,
		},
		"Posture Change Detected": {
			color: "#27ae60",
			label: "Detected",
			shape: RotateDetectedIcon,
			yValue: 350,
		},
		"Movement Detected": {
			color: "#e67e22",
			label: "Hareket Algılandı",
			shape: MovementIcon,
			yValue: 400,
		},
		"Fall Detected": {
			color: "#c0392b",
			label: "Düşme Algılandı",
			shape: FallIcon,
			yValue: 450,
		},
		"Presence Detected": {
			color: "#27ae60",
			label: "Varlık Algılandı",
			shape: PersonIcon,
			yValue: 500,
		},
		"No Presence": {
			color: "#95a5a6",
			label: "Varlık Yok",
			shape: NoPersonIcon,
			yValue: 550,
		},
		"In Bed": {
			color: "#00ff00",
			label: "Yatakta",
			shape: InBedIcon,
			yValue: 50,
		},
		awayFromBed: {
			color: "#3498db",
			label: "Yataktan Uzakta",
			shape: AwayFromBedIcon,
			yValue: 600,
		},
	};

	// VitalDetail.jsx'teki formatDetails fonksiyonu ile aynı mantık
	const formatDetails = (details) => {
		if (!details) return "";

		if (details.toLowerCase().includes("above")) {
			const match = details.match(/(\d+)\s*(bpm|rpm)/i);
			if (match) {
				const number = match[1];
				const unit = match[2].toLowerCase();
				return `${number} ${unit}'nin üzerinde`;
			}
			return details.replace(/above/gi, "üzerinde");
		}

		if (details.toLowerCase().includes("below")) {
			const match = details.match(/(\d+)\s*(bpm|rpm)/i);
			if (match) {
				const number = match[1];
				const unit = match[2].toLowerCase();
				return `${number} ${unit}'in altında`;
			}
			return details.replace(/below/gi, "altında");
		}

		return details;
	};

	// VitalDetail.jsx'teki tablo mantığına göre label belirleme
	const getEventLabel = (item) => {
		if (item.occupancy === "inBed") {
			return "Yatağa Girdi";
		}
		if (item.occupancy === "outOfBed" || item.title === "Out Of Bed") {
			return "Yataktan Çıktı";
		}
		if (item.title === "High Heart Rate") {
			return `Yüksek kalp atış hızı${
				item.details ? ` (${formatDetails(item.details)})` : ""
			}`;
		}
		if (item.title === "Low Heart Rate") {
			return `Düşük kalp atış hızı${
				item.details ? ` (${formatDetails(item.details)})` : ""
			}`;
		}
		if (item.title === "High Respiration Rate") {
			return `Yüksek Solunum Hızı${
				item.details ? ` (${formatDetails(item.details)})` : ""
			}`;
		}
		if (item.title === "Low Respiration Rate") {
			return `Düşük Solunum Hızı${
				item.details ? ` (${formatDetails(item.details)})` : ""
			}`;
		}
		if (!item.occupancy && item.title === "Posture Change") {
			if (
				item.details === "Turn left to prevent pressure ulcers" ||
				item.details === "Turn to back to prevent pressure ulcers" ||
				item.details === "Turn right to prevent pressure ulcers"
			) {
				return "Bası yaralarını önlemek için pozisyon verin";
			}
			return item.details || "Pozisyon Değişimi";
		}
		// Detected türleri
		if (
			item.title === "Movement Detected" ||
			item.title === "movementDetected"
		) {
			return "Hareket Algılandı";
		}
		if (
			item.title === "Fall Detected" ||
			item.title === "fallDetected" ||
			item.title === "falldetected"
		) {
			return "Düşme Algılandı";
		}
		if (
			item.title === "Presence Detected" ||
			item.title === "presenceDetected"
		) {
			return "Varlık Algılandı";
		}
		if (item.title === "No Presence" || item.title === "noPresence") {
			return "Varlık Yok";
		}
		if (item.title === "In Bed") {
			return "Yatakta";
		}
		if (item.title === "Away From Bed" || item.occupancy === "awayFromBed") {
			return "Yataktan Uzakta";
		}
		return item.occupancy || item.title || "Bilinmeyen Olay";
	};

	// VitalDetail.jsx'teki tablo mantığına göre event türünü belirleme
	const getEventType = (item) => {
		if (item.occupancy === "inBed") {
			return "inBed";
		}
		if (item.occupancy === "outOfBed" || item.title === "Out Of Bed") {
			return "outOfBed";
		}
		if (item.title === "High Heart Rate") {
			return "High Heart Rate";
		}
		if (item.title === "Low Heart Rate") {
			return "Low Heart Rate";
		}
		if (item.title === "High Respiration Rate") {
			return "High Respiration Rate";
		}
		if (item.title === "Low Respiration Rate") {
			return "Low Respiration Rate";
		}
		if (!item.occupancy && item.title === "Posture Change") {
			// "Detected" durumu için ayrı event türü
			if (item.details === "Detected") {
				return "Posture Change Detected";
			}
			return "Posture Change";
		}
		return item.occupancy || item.title || "unknown";
	};

	const chartData = useMemo(() => {
		if (!data || !Array.isArray(data) || data.length === 0) return [];

		return data
			.filter((item) => {
				// VitalDetail.jsx'teki tabloda gösterilen verilere göre filtrele
				if (!item.time) return false;

				return (
					item.occupancy === "inBed" ||
					item.occupancy === "outOfBed" ||
					item.title === "Out Of Bed" ||
					item.title === "High Heart Rate" ||
					item.title === "Low Heart Rate" ||
					item.title === "High Respiration Rate" ||
					item.title === "Low Respiration Rate" ||
					item.title === "Posture Change"
				);
			})
			.map((item) => {
				const eventType = getEventType(item);
				const cfg = eventConfig[eventType] ?? {
					color: "#888888",
					label: eventType,
					shape: CircleShape,
					yValue: 400,
				};

				const date = new Date(item.time);
				return {
					time: date.getTime(),
					timeLabel: date.toLocaleString("tr-TR", {
						year: "numeric",
						month: "long",
						day: "numeric",
						hour: "2-digit",
						minute: "2-digit",
						second: "2-digit",
					}),
					label: getEventLabel(item),
					value: cfg.yValue,
					color: cfg.color,
					type: eventType,
				};
			})
			.sort((a, b) => a.time - b.time);
	}, [data]);

	// Event tiplerine göre grupla
	const groupedData = useMemo(() => {
		if (!chartData || chartData.length === 0) return {};

		const groups = {};
		chartData.forEach((item) => {
			if (!groups[item.type]) {
				groups[item.type] = [];
			}
			groups[item.type].push(item);
		});
		return groups;
	}, [chartData]);

	// X ekseni için padding hesapla (verilerin kenarlardan uzak durması için)
	const xAxisPadding = useMemo(() => {
		if (chartData.length < 2) return 0;
		const times = chartData.map((item) => item.time);
		const minTime = Math.min(...times);
		const maxTime = Math.max(...times);
		const range = maxTime - minTime;
		// Toplam aralığın %3'ü kadar padding ekle
		return range * 0.03;
	}, [chartData]);

	// Zoom fonksiyonları - Hook'lar early return'den önce olmalı
	const handleMouseDown = useCallback((e) => {
		if (e && e.activeLabel) {
			setRefAreaLeft(e.activeLabel);
			setIsZooming(true);
		}
	}, []);

	const handleMouseMove = useCallback(
		(e) => {
			if (isZooming && e && e.activeLabel) {
				setRefAreaRight(e.activeLabel);
			}
		},
		[isZooming]
	);

	const handleMouseUp = useCallback(() => {
		if (refAreaLeft && refAreaRight) {
			const left = Math.min(refAreaLeft, refAreaRight);
			const right = Math.max(refAreaLeft, refAreaRight);

			// Seçilen alandaki verileri bul
			const filteredData = chartData.filter(
				(item) => item.time >= left && item.time <= right
			);

			if (filteredData.length > 0) {
				// Y değerlerini bul
				const yValues = filteredData.map((item) => item.value);
				const minY = Math.min(...yValues) - 25;
				const maxY = Math.max(...yValues) + 25;

				setXDomain([left, right]);
				setYDomain([Math.max(0, minY), maxY]);
			}
		}

		setRefAreaLeft(null);
		setRefAreaRight(null);
		setIsZooming(false);
	}, [refAreaLeft, refAreaRight, chartData]);

	const handleZoomReset = useCallback(() => {
		setXDomain(null);
		setYDomain(null);
		setZoomLevel(1);
	}, []);

	// Zoom butonları
	const zoomIn = useCallback(() => {
		if (chartData.length === 0) return;

		const times = chartData.map((item) => item.time);
		const dataMinTime = Math.min(...times);
		const dataMaxTime = Math.max(...times);
		const dataRange = dataMaxTime - dataMinTime;

		const newZoomLevel = Math.min(zoomLevel * 1.5, 10); // Max 10x zoom
		setZoomLevel(newZoomLevel);

		const zoomRange = dataRange / newZoomLevel;
		const center = xDomain
			? (xDomain[0] + xDomain[1]) / 2
			: (dataMinTime + dataMaxTime) / 2;

		let newMin = center - zoomRange / 2;
		let newMax = center + zoomRange / 2;

		// Sınırları kontrol et
		if (newMin < dataMinTime) {
			newMin = dataMinTime;
			newMax = dataMinTime + zoomRange;
		}
		if (newMax > dataMaxTime) {
			newMax = dataMaxTime;
			newMin = dataMaxTime - zoomRange;
		}

		setXDomain([newMin, newMax]);
	}, [xDomain, chartData, zoomLevel]);

	const zoomOut = useCallback(() => {
		if (chartData.length === 0) return;

		const times = chartData.map((item) => item.time);
		const dataMinTime = Math.min(...times);
		const dataMaxTime = Math.max(...times);
		const dataRange = dataMaxTime - dataMinTime;

		const newZoomLevel = Math.max(zoomLevel / 1.5, 1);
		setZoomLevel(newZoomLevel);

		if (newZoomLevel <= 1) {
			setXDomain(null);
			setYDomain(null);
			return;
		}

		const zoomRange = dataRange / newZoomLevel;
		const center = xDomain
			? (xDomain[0] + xDomain[1]) / 2
			: (dataMinTime + dataMaxTime) / 2;

		let newMin = center - zoomRange / 2;
		let newMax = center + zoomRange / 2;

		// Sınırları kontrol et
		if (newMin < dataMinTime) {
			newMin = dataMinTime;
			newMax = dataMinTime + zoomRange;
		}
		if (newMax > dataMaxTime) {
			newMax = dataMaxTime;
			newMin = dataMaxTime - zoomRange;
		}

		setXDomain([newMin, newMax]);
	}, [xDomain, chartData, zoomLevel]);

	// Sola/Sağa kaydırma
	const panLeft = useCallback(() => {
		if (!xDomain || chartData.length === 0) return;

		const times = chartData.map((item) => item.time);
		const dataMinTime = Math.min(...times);
		const range = xDomain[1] - xDomain[0];
		const panAmount = range * 0.3;

		const newMin = Math.max(dataMinTime, xDomain[0] - panAmount);
		const newMax = newMin + range;

		setXDomain([newMin, newMax]);
	}, [xDomain, chartData]);

	const panRight = useCallback(() => {
		if (!xDomain || chartData.length === 0) return;

		const times = chartData.map((item) => item.time);
		const dataMaxTime = Math.max(...times);
		const range = xDomain[1] - xDomain[0];
		const panAmount = range * 0.3;

		const newMax = Math.min(dataMaxTime, xDomain[1] + panAmount);
		const newMin = newMax - range;

		setXDomain([newMin, newMax]);
	}, [xDomain, chartData]);

	const CustomTooltip = ({ active, payload }) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload;
			return (
				<div
					style={{
						backgroundColor: "rgba(255, 255, 255, 0.95)",
						padding: "10px",
						border: `2px solid ${data.color}`,
						borderRadius: "8px",
						boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
						maxWidth: "280px",
					}}
				>
					<p style={{ margin: 0, fontWeight: "bold", color: data.color }}>
						{data.label}
					</p>
					<p style={{ margin: "5px 0 0 0", fontSize: "12px", color: "#666" }}>
						{data.timeLabel}
					</p>
				</div>
			);
		}
		return null;
	};

	const CustomLegend = ({ payload }) => {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					gap: "20px",
					marginTop: "20px",
					flexWrap: "wrap",
				}}
			>
				{payload.map((entry, index) => (
					<div
						key={index}
						style={{
							display: "flex",
							alignItems: "center",
							gap: "8px",
						}}
					>
						<div
							style={{
								width: "16px",
								height: "16px",
								backgroundColor: entry.color,
								borderRadius: "4px",
								border: `2px solid ${entry.color}`,
							}}
						/>
						<span style={{ fontSize: "14px", color: "#333" }}>
							{entry.value}
						</span>
					</div>
				))}
			</div>
		);
	};

	if (!chartData || chartData.length === 0) {
		return (
			<div
				style={{
					width: "100%",
					height: "500px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					color: "#999",
				}}
			>
				<p>Gösterilecek alarm verisi bulunamadı.</p>
			</div>
		);
	}

	const formatXAxis = (tickItem) => {
		return new Date(tickItem).toLocaleString("tr-TR", {
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	// Y ekseni için custom tick fonksiyonu
	const formatYAxis = (tickItem) => {
		const typeMap = {
			50: "Yatağa Girdi",
			100: "Yataktan Çıktı",
			150: "Yüksek Kalp Atış",
			200: "Düşük Kalp Atış",
			250: "Yüksek Solunum",
			300: "Düşük Solunum",
			350: "Pozisyon Değişimi",
		};
		return typeMap[tickItem] || "";
	};

	// Zoom seviyesi metni
	const getZoomText = () => {
		if (zoomLevel <= 1) return "100%";
		return `${zoomLevel.toFixed(1)}x`;
	};

	// Tema uyumlu buton stilleri
	const buttonStyles = {
		base: {
			padding: "8px 14px",
			border: "none",
			borderRadius: "6px",
			cursor: "pointer",
			fontSize: "14px",
			fontWeight: "500",
			transition: "all 0.2s ease",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			minWidth: "40px",
		},
		primary: {
			backgroundColor: "#16a085",
			color: "white",
		},
		primaryDisabled: {
			backgroundColor: "#a0aec0",
			color: "white",
			cursor: "not-allowed",
		},
		secondary: {
			backgroundColor: "#4a5568",
			color: "white",
		},
		secondaryDisabled: {
			backgroundColor: "#cbd5e0",
			color: "white",
			cursor: "not-allowed",
		},
		danger: {
			backgroundColor: "#e53e3e",
			color: "white",
		},
		dangerDisabled: {
			backgroundColor: "#feb2b2",
			color: "white",
			cursor: "not-allowed",
		},
	};

	return (
		<div style={{ width: "100%", height: "600px" }}>
			{/* Zoom Kontrolleri */}
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					gap: "8px",
					marginBottom: "12px",
					flexWrap: "wrap",
					alignItems: "center",
					padding: "10px",
					backgroundColor: "#f7fafc",
					borderRadius: "8px",
					border: "1px solid #e2e8f0",
				}}
			>
				{/* Sol kaydırma */}
				<button
					onClick={panLeft}
					disabled={!xDomain}
					title="Sola Kaydır"
					style={{
						...buttonStyles.base,
						...(xDomain
							? buttonStyles.secondary
							: buttonStyles.secondaryDisabled),
					}}
				>
					◀
				</button>

				{/* Uzaklaştır */}
				<button
					onClick={zoomOut}
					disabled={zoomLevel <= 1}
					title="Uzaklaştır"
					style={{
						...buttonStyles.base,
						...(zoomLevel > 1
							? buttonStyles.primary
							: buttonStyles.primaryDisabled),
						fontSize: "18px",
						fontWeight: "bold",
					}}
				>
					−
				</button>

				{/* Zoom seviyesi göstergesi */}
				<div
					style={{
						padding: "8px 16px",
						backgroundColor: xDomain ? "#16a085" : "#edf2f7",
						color: xDomain ? "white" : "#4a5568",
						borderRadius: "6px",
						fontSize: "14px",
						minWidth: "70px",
						textAlign: "center",
						fontWeight: "600",
						border: xDomain ? "none" : "1px solid #e2e8f0",
					}}
				>
					🔍 {getZoomText()}
				</div>

				{/* Yakınlaştır */}
				<button
					onClick={zoomIn}
					disabled={zoomLevel >= 10}
					title="Yakınlaştır"
					style={{
						...buttonStyles.base,
						...(zoomLevel < 10
							? buttonStyles.primary
							: buttonStyles.primaryDisabled),
						fontSize: "18px",
						fontWeight: "bold",
					}}
				>
					+
				</button>

				{/* Sağ kaydırma */}
				<button
					onClick={panRight}
					disabled={!xDomain}
					title="Sağa Kaydır"
					style={{
						...buttonStyles.base,
						...(xDomain
							? buttonStyles.secondary
							: buttonStyles.secondaryDisabled),
					}}
				>
					▶
				</button>

				{/* Sıfırla */}
				<button
					onClick={handleZoomReset}
					disabled={!xDomain}
					title="Sıfırla"
					style={{
						...buttonStyles.base,
						...(xDomain ? buttonStyles.danger : buttonStyles.dangerDisabled),
						marginLeft: "8px",
					}}
				>
					↺ Sıfırla
				</button>
			</div>

			<ResponsiveContainer width="100%" height="90%">
				<ScatterChart
					margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
					onMouseDown={handleMouseDown}
					onMouseMove={handleMouseMove}
					onMouseUp={handleMouseUp}
					onMouseLeave={() => {
						if (isZooming) {
							setRefAreaLeft(null);
							setRefAreaRight(null);
							setIsZooming(false);
						}
					}}
				>
					<CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
					<XAxis
						type="number"
						dataKey="time"
						domain={xDomain || [
							(dataMin) => dataMin - xAxisPadding,
							(dataMax) => dataMax + xAxisPadding,
						]}
						tickFormatter={formatXAxis}
						stroke="#666"
						style={{ fontSize: "12px" }}
						allowDataOverflow
					/>
					<YAxis
						type="number"
						dataKey="value"
						domain={yDomain || [0, 400]}
						stroke="#666"
						style={{ fontSize: "12px" }}
						tickFormatter={formatYAxis}
						ticks={yDomain ? undefined : [50, 100, 150, 200, 250, 300, 350]}
						allowDecimals={false}
						allowDataOverflow
						label={{
							value: "Olay Tipi",
							angle: -90,
							position: "insideLeft",
							style: { textAnchor: "middle", fill: "#666" },
						}}
					/>
					<Tooltip
						content={<CustomTooltip />}
						cursor={{ strokeDasharray: "3 3" }}
						wrapperStyle={{ zIndex: 1000 }}
						isAnimationActive={false}
					/>
					{Object.keys(groupedData).map((type) => {
						const cfg = eventConfig[type] || {
							color: "#888888",
							label: type,
							shape: CircleShape,
							yValue: 400,
						};
						// Zoom yapıldığında sadece görünür verileri göster
						const visibleData = xDomain
							? groupedData[type].filter(
									(d) => d.time >= xDomain[0] && d.time <= xDomain[1]
							  )
							: groupedData[type];

						return (
							<Scatter
								key={type}
								name={cfg.label}
								data={visibleData}
								fill={cfg.color}
								shape={cfg.shape}
								isAnimationActive={false}
							>
								{visibleData.map((entry, idx) => (
									<Cell
										key={`cell-${type}-${idx}`}
										fill={entry.color}
										stroke={entry.color}
										strokeWidth={2}
									/>
								))}
							</Scatter>
						);
					})}

				{/* Zoom seçim alanı */}
				{refAreaLeft && refAreaRight && (
					<ReferenceArea
						x1={refAreaLeft}
						x2={refAreaRight}
						strokeOpacity={0.3}
						fill="#16a085"
						fillOpacity={0.2}
					/>
				)}

					<Legend content={<CustomLegend />} />
				</ScatterChart>
			</ResponsiveContainer>
		</div>
	);
};

export default EventChart;
